import React, { useState, useEffect } from 'react';
import { 
  DevicePhoneMobileIcon, 
  PlusIcon, 
  PencilIcon, 
  TrashIcon,
  EyeIcon,
  EyeSlashIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  CheckCircleIcon
} from '@heroicons/react/24/outline';

const MobileControl = () => {
  const [updates, setUpdates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingUpdate, setEditingUpdate] = useState(null);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('success'); // 'success' or 'error'
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    urgency: 'none',
    features: [],
    bugFixes: [],
    playStoreUrl: '',
    appStoreUrl: '',
    currentVersion: '1.0.0',
    minVersion: '',
    maxVersion: ''
  });
  
  // Auto-hide message after 5 seconds
  useEffect(() => {
    if (message) {
      const timer = setTimeout(() => {
        setMessage('');
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [message]);

  useEffect(() => {
    fetchUpdates();
  }, []);

  const fetchUpdates = async () => {
    try {
      setLoading(true);
      const response = await fetch('https://backend.jhonks.com/api/app-updates', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch app updates');
      }
      
      const data = await response.json();
      if (data.success) {
        setUpdates(data.data);
      } else {
        setMessage(data.message || 'Failed to fetch app updates');
        setMessageType('error');
      }
    } catch (error) {
      console.error('Error fetching updates:', error);
      setMessage(error.message || 'Failed to fetch app updates. Please try again.');
      setMessageType('error');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateUpdate = async (e) => {
    e.preventDefault();
    
    // Validate version format
    const versionPattern = /^\d+\.\d+\.\d+$/;
    if (!versionPattern.test(formData.currentVersion)) {
      setMessage('Invalid current version format. Use semantic versioning (e.g., 1.0.0)');
      setMessageType('error');
      return;
    }
    
    if (formData.minVersion && !versionPattern.test(formData.minVersion)) {
      setMessage('Invalid min version format. Use semantic versioning (e.g., 1.0.0)');
      setMessageType('error');
      return;
    }
    
    if (formData.maxVersion && !versionPattern.test(formData.maxVersion)) {
      setMessage('Invalid max version format. Use semantic versioning (e.g., 1.0.0)');
      setMessageType('error');
      return;
    }
    
    try {
      const response = await fetch('https://backend.jhonks.com/api/app-updates', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          ...formData,
          minVersion: formData.minVersion || null,
          maxVersion: formData.maxVersion || null
        })
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.message || 'Failed to create app update');
      }
      
      if (data.success) {
        setMessage('App update created successfully!');
        setMessageType('success');
        setShowCreateModal(false);
        resetForm();
        fetchUpdates();
      } else {
        setMessage(data.message || 'Failed to create app update');
        setMessageType('error');
      }
    } catch (error) {
      console.error('Error creating update:', error);
      setMessage(error.message || 'Failed to create app update. Please try again.');
      setMessageType('error');
    }
  };

  const handleUpdateUpdate = async (e) => {
    e.preventDefault();
    
    // Validate version format
    const versionPattern = /^\d+\.\d+\.\d+$/;
    if (!versionPattern.test(formData.currentVersion)) {
      setMessage('Invalid current version format. Use semantic versioning (e.g., 1.0.0)');
      setMessageType('error');
      return;
    }
    
    if (formData.minVersion && !versionPattern.test(formData.minVersion)) {
      setMessage('Invalid min version format. Use semantic versioning (e.g., 1.0.0)');
      setMessageType('error');
      return;
    }
    
    if (formData.maxVersion && !versionPattern.test(formData.maxVersion)) {
      setMessage('Invalid max version format. Use semantic versioning (e.g., 1.0.0)');
      setMessageType('error');
      return;
    }
    
    try {
      const response = await fetch(`https://backend.jhonks.com/api/app-updates/${editingUpdate._id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          ...formData,
          minVersion: formData.minVersion || null,
          maxVersion: formData.maxVersion || null
        })
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.message || 'Failed to update app update');
      }
      
      if (data.success) {
        setMessage('App update updated successfully!');
        setMessageType('success');
        setEditingUpdate(null);
        resetForm();
        fetchUpdates();
      } else {
        setMessage(data.message || 'Failed to update app update');
        setMessageType('error');
      }
    } catch (error) {
      console.error('Error updating update:', error);
      setMessage(error.message || 'Failed to update app update. Please try again.');
      setMessageType('error');
    }
  };

  const handleDeleteUpdate = async (id) => {
    if (window.confirm('Are you sure you want to delete this update?')) {
      try {
        const response = await fetch(`https://backend.jhonks.com/api/app-updates/${id}`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        
        const data = await response.json();
        
        if (!response.ok) {
          throw new Error(data.message || 'Failed to delete app update');
        }
        
        if (data.success) {
          setMessage('App update deleted successfully!');
          setMessageType('success');
          fetchUpdates();
        } else {
          setMessage(data.message || 'Failed to delete app update');
          setMessageType('error');
        }
      } catch (error) {
        console.error('Error deleting update:', error);
        setMessage(error.message || 'Failed to delete app update. Please try again.');
        setMessageType('error');
      }
    }
  };

  const handleToggleStatus = async (id) => {
    try {
      const response = await fetch(`https://backend.jhonks.com/api/app-updates/${id}/toggle`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.message || 'Failed to toggle update status');
      }
      
      if (data.success) {
        setMessage(data.message || 'Update status changed successfully!');
        setMessageType('success');
        fetchUpdates();
      } else {
        setMessage(data.message || 'Failed to toggle update status');
        setMessageType('error');
      }
    } catch (error) {
      console.error('Error toggling update status:', error);
      setMessage(error.message || 'Failed to toggle update status. Please try again.');
      setMessageType('error');
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      urgency: 'none',
      features: [],
      bugFixes: [],
      playStoreUrl: '',
      appStoreUrl: '',
      currentVersion: '1.0.0',
      minVersion: '',
      maxVersion: ''
    });
  };

  const openEditModal = (update) => {
    setEditingUpdate(update);
    setFormData({
      title: update.title,
      description: update.description,
      urgency: update.urgency,
      features: update.features || [],
      bugFixes: update.bugFixes || [],
      playStoreUrl: update.playStoreUrl || '',
      appStoreUrl: update.appStoreUrl || '',
      currentVersion: update.currentVersion || '1.0.0',
      minVersion: update.minVersion || '',
      maxVersion: update.maxVersion || ''
    });
  };

  const getUrgencyIcon = (urgency) => {
    switch (urgency) {
      case 'critical':
        return <ExclamationTriangleIcon className="h-5 w-5 text-red-500" />;
      case 'optional':
        return <InformationCircleIcon className="h-5 w-5 text-blue-500" />;
      default:
        return <CheckCircleIcon className="h-5 w-5 text-gray-500" />;
    }
  };

  const getUrgencyColor = (urgency) => {
    switch (urgency) {
      case 'critical':
        return 'bg-red-100 text-red-800';
      case 'optional':
        return 'bg-blue-100 text-blue-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div className="flex items-center space-x-3">
          <DevicePhoneMobileIcon className="h-8 w-8 text-green-600" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Mobile App Control</h1>
            <p className="text-gray-600">Manage app updates and notifications</p>
          </div>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="flex items-center space-x-2 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors"
        >
          <PlusIcon className="h-5 w-5" />
          <span>Create Update</span>
        </button>
      </div>

      {/* Notification Message */}
      {message && (
        <div
          className={`p-4 rounded-md border ${
            messageType === 'success'
              ? 'bg-green-100 text-green-700 border-green-200'
              : 'bg-red-100 text-red-700 border-red-200'
          }`}
        >
          <div className="flex items-center">
            {messageType === 'success' ? (
              <CheckCircleIcon className="h-5 w-5 mr-2" />
            ) : (
              <ExclamationTriangleIcon className="h-5 w-5 mr-2" />
            )}
            <span>{message}</span>
            <button
              onClick={() => setMessage('')}
              className="ml-auto text-gray-500 hover:text-gray-700"
            >
              <span className="sr-only">Close</span>
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      )}


      {/* Updates List */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">App Updates</h2>
        </div>
        <div className="divide-y divide-gray-200">
          {updates.map((update) => (
            <div key={update._id} className="p-6">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-3 mb-2">
                    <h3 className="text-lg font-semibold text-gray-900">{update.title}</h3>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getUrgencyColor(update.urgency)}`}>
                      {update.urgency}
                    </span>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${update.isActive ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                      {update.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                  <p className="text-gray-600 mb-2">{update.description}</p>
                  <div className="flex items-center space-x-4 text-sm text-gray-500 mb-2">
                    <span>Released: {new Date(update.releaseDate).toLocaleDateString()}</span>
                    <span>Current Version: {update.currentVersion || '1.0.0'}</span>
                    {update.minVersion && <span>Min Version: {update.minVersion}</span>}
                    {update.maxVersion && <span>Max Version: {update.maxVersion}</span>}
                  </div>
                  <div className="text-xs text-gray-400 space-y-1">
                    <div>Play Store: {update.playStoreUrl ? '✓ Set' : '✗ Missing'}</div>
                    <div>App Store: {update.appStoreUrl ? '✓ Set' : '✗ Missing'}</div>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => handleToggleStatus(update._id)}
                    className={`p-2 rounded-lg ${update.isActive ? 'text-green-600 hover:bg-green-50' : 'text-gray-400 hover:bg-gray-50'}`}
                    title={update.isActive ? 'Deactivate' : 'Activate'}
                  >
                    {update.isActive ? <EyeIcon className="h-5 w-5" /> : <EyeSlashIcon className="h-5 w-5" />}
                  </button>
                  <button
                    onClick={() => openEditModal(update)}
                    className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg"
                    title="Edit"
                  >
                    <PencilIcon className="h-5 w-5" />
                  </button>
                  <button
                    onClick={() => handleDeleteUpdate(update._id)}
                    className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                    title="Delete"
                  >
                    <TrashIcon className="h-5 w-5" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Create/Edit Modal */}
      {(showCreateModal || editingUpdate) && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-bold mb-4">
              {editingUpdate ? 'Edit Update' : 'Create New Update'}
            </h2>
            <form onSubmit={editingUpdate ? handleUpdateUpdate : handleCreateUpdate} className="space-y-4">

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({...formData, title: e.target.value})}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500"
                  rows="3"
                  required
                />
              </div>


              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                <h3 className="text-sm font-semibold text-blue-900 mb-2">Version Control Guide</h3>
                <ul className="text-xs text-blue-800 space-y-1 list-disc list-inside">
                  <li><strong>Current Version:</strong> The latest app version. Users with this version will NOT see the update modal.</li>
                  <li><strong>Min Version (Optional):</strong> Only users with version &gt;= Min Version will see the update. Leave empty to show to all versions below Current Version.</li>
                  <li><strong>Max Version (Optional):</strong> Only users with version &lt; Max Version will see the update. Leave empty to show to all versions below Current Version.</li>
                  <li><strong>Example:</strong> Current: 1.0.0, Min: 0.8.0, Max: 0.9.9 means users with versions 0.8.0 to 0.9.8 will see the update, but not users with 1.0.0</li>
                </ul>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Current Version *</label>
                  <input
                    type="text"
                    value={formData.currentVersion}
                    onChange={(e) => setFormData({...formData, currentVersion: e.target.value})}
                    placeholder="1.0.0"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500"
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1">Users with this version will NOT see the update modal</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Urgency *</label>
                  <select
                    value={formData.urgency}
                    onChange={(e) => setFormData({...formData, urgency: e.target.value})}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500"
                  >
                    <option value="none">No Update Required</option>
                    <option value="optional">Optional Update (Dismissible)</option>
                    <option value="critical">Critical Update (Non-Dismissible)</option>
                  </select>
                  <p className="text-xs text-gray-500 mt-1">
                    {formData.urgency === 'critical' && 'Users cannot dismiss this modal - must update'}
                    {formData.urgency === 'optional' && 'Users can dismiss this modal'}
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Min Version (Optional)</label>
                  <input
                    type="text"
                    value={formData.minVersion}
                    onChange={(e) => setFormData({...formData, minVersion: e.target.value})}
                    placeholder="0.9.0"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500"
                  />
                  <p className="text-xs text-gray-500 mt-1">Minimum version that should see this update (inclusive). Leave empty for all versions below Current Version.</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Max Version (Optional)</label>
                  <input
                    type="text"
                    value={formData.maxVersion}
                    onChange={(e) => setFormData({...formData, maxVersion: e.target.value})}
                    placeholder="0.9.9"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500"
                  />
                  <p className="text-xs text-gray-500 mt-1">Maximum version that should see this update (exclusive). Leave empty for all versions below Current Version.</p>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Play Store URL (Android) *</label>
                <input
                  type="url"
                  value={formData.playStoreUrl}
                  onChange={(e) => setFormData({...formData, playStoreUrl: e.target.value})}
                  placeholder="https://play.google.com/store/apps/details?id=com.jhonks.Jhonks"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">App Store URL (iOS) (Optional)</label>
                <input
                  type="url"
                  value={formData.appStoreUrl}
                  onChange={(e) => setFormData({...formData, appStoreUrl: e.target.value})}
                  placeholder="https://apps.apple.com/app/id123456789"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500"
                />
                <p className="text-xs text-gray-500 mt-1">Leave empty if iOS app is not yet published</p>
              </div>

              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowCreateModal(false);
                    setEditingUpdate(null);
                    resetForm();
                  }}
                  className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                >
                  {editingUpdate ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default MobileControl;
